import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps } from 'react-router-dom'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonModal,
} from '@ionic/react'
import { useMutation } from '@tanstack/react-query'

import { BackButton } from '@components/atoms/BackButton'
import { ContestEditForm } from '@components/organisms/ContestEditForm'
import { ContestCreationConfirmationAlert } from '@/components/molecules/Alert/ContestCreationConfirmation'
import { ContestCreateUploadVideo } from '@/components/organisms/ContestCreateUploadVideo'

import {
  createContest,
  IContestConfiguration,
  joinContest,
} from '@services/contest'
import { errorBoundary } from '@/services/errorBoundary'
import { useFileUpload } from '@/lib/hooks/useFileUpload'
import { useUserInfo } from '@/lib/hooks/useUserInfo'
import { Routes } from '@/router/routes'
import { hoursToMilliseconds } from '@utils/date'

import { ContestType } from '@enums'

import { contestTypes } from './contestTypes'
export const ContestEditor = ({ history }: RouteComponentProps) => {
  const { t } = useTranslation('common')
  const { upload, progressPercentage, isUploading } = useFileUpload()
  const inputFileRef = useRef<HTMLInputElement>(null)
  const contestType = contestTypes.find(
    (contest) => contest.contestType === ContestType.PSSV
  )

  const { data: userInfo } = useUserInfo()

  const [contestData, setContestData] = useState<IContestConfiguration | null>(
    null
  )
  const [videoUrl, setVideoUrl] = useState<string>('')
  const [confirmationOpen, setConfirmationOpen] = useState<boolean>(false)

  const onFileChange = (event) => {
    const target = event.target as HTMLInputElement
    const file = target?.files?.[0]

    if (!file) {
      return
    }

    setVideoUrl(URL.createObjectURL(file))
  }

  const { mutateAsync: createContestMutation, isPending: mutationLoading } =
    useMutation({
      mutationKey: ['createContest'],
      mutationFn: createContest,
      onError: errorBoundary,
    })

  const { mutate: addVideoDescriptionMutation } = useMutation({
    mutationKey: ['joinContest'],
    mutationFn: joinContest,
  })

  const [present, dismiss] = useIonModal(ContestCreateUploadVideo, {
    onDismiss: () => onDismiss(),
    onModalComplete: async (videoDescription) => {
      const data = await createContestMutation({
        ...contestType,
        userId: userInfo.id,
        submissionStartOffsetTime: hoursToMilliseconds(
          contestType.submissionStartOffsetTime
        ),
        votingStartOffsetTime: hoursToMilliseconds(
          contestType.submissionStartOffsetTime
        ),
        contestResultOffsetTime: hoursToMilliseconds(
          contestType.contestResultOffsetTime
        ),
        ...contestData,
      } as IContestConfiguration)

      addVideoDescriptionMutation({
        contestId: data?.id,
        description: videoDescription,
      })
      const video = `users/${userInfo.id}/videos/${data?.id}`
      if (!videoUrl) {
        return
      }
      const blob = await fetch(videoUrl).then((res) => res.blob())
      upload({ fileName: video, file: blob })
      setVideoUrl('')
      setConfirmationOpen(true)
      dismiss()
    },
    progressPercentage,
    videoDataUrl: videoUrl,
    contestData,
    user: userInfo,
    isLoading: isUploading,
  })

  useEffect(() => {
    if (videoUrl) {
      present()
    }
  }, [videoUrl, present])

  const onSubmit = async (data) => {
    setContestData(data)
    if (!videoUrl) {
      if (inputFileRef.current) {
        inputFileRef.current.click()
      }
      return
    }
    if (!userInfo) return
    return
  }

  const onDismiss = async () => {
    dismiss()
  }

  const handleAlertDismiss = () => {
    history.push(Routes.DISCOVER)
  }

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonButtons slot="start">
            <BackButton defaultHref="/feed" />
          </IonButtons>
          <IonTitle class="text-center">{t('contest.edit.title')}</IonTitle>
          <IonButtons slot="end">
            <IonButton>
              <IonIcon slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <div className="max-w-md mx-auto px-6 pt-8">
          <input
            type="file"
            multiple={false}
            accept="video/*"
            ref={inputFileRef}
            onChange={onFileChange}
            className={'hidden'}
            onClick={(e) => {
              const target = e.target as HTMLInputElement
              target.value = null
            }}
          />
          {!confirmationOpen ? (
            <ContestEditForm onSubmit={onSubmit} isLoading={mutationLoading} />
          ) : (
            <ContestCreationConfirmationAlert
              isOpen={confirmationOpen}
              contestName={contestData?.contestName}
              startDate={Number(contestData?.goesOnline)}
              onDismiss={handleAlertDismiss}
            />
          )}
        </div>
      </IonContent>
    </IonPage>
  )
}
